/**
 * AI Workforce — Phase 1 complete (Sub-Phases 1A → 1D).
 *
 * Admin-only page rendering:
 *   • Overview stats (7 counters)
 *   • Clickable 7-agent grid + Employee Detail sheet (6 tabs)
 *   • Task creation card + Task list
 *   • Task Detail sheet (CEO plan + children + messages + result)
 *   • Approvals sheet + inline Approve/Reject/Edit
 *   • Recent Activity feed
 *
 * Guard: non-admins are silently redirected to /app. All data comes from
 * /api/admin/workforce/* — no metric is faked client-side.
 */
import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Bot, Briefcase, Users, Activity, CheckCircle2, XCircle, Clock, Loader2,
  Crown, Search, Megaphone, DollarSign, Headphones, Code2, TrendingUp,
  Plus, RefreshCw, Play, Pause, X, RotateCcw, MessageSquare, Brain, Settings2,
  ThumbsUp, ThumbsDown, ChevronRight, ClipboardList, Sparkles, Send,
  Target, ExternalLink, Archive,
} from "lucide-react";

const AGENT_ICON = {
  ceo: Crown, research: Search, marketing: Megaphone, sales: TrendingUp,
  support: Headphones, developer: Code2, finance: DollarSign,
};

const STATUS_STYLE = {
  idle: { key: "wf_status_idle", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  working: { key: "wf_status_working", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  waiting_for_approval: { key: "wf_status_waiting_for_approval", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  paused: { key: "wf_status_paused", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  error: { key: "wf_status_error", cls: "bg-red-50 text-red-700 border-red-200" },
  offline: { key: "wf_status_offline", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

const TASK_STATUS_STYLE = {
  pending: "bg-zinc-100 text-zinc-700 border-zinc-200",
  planning: "bg-indigo-50 text-indigo-700 border-indigo-200",
  assigned: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  waiting_for_approval: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
  paused: "bg-purple-50 text-purple-700 border-purple-200",
};

function StatCard({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = { default: "text-zinc-900", blue: "text-blue-700", amber: "text-amber-700", emerald: "text-emerald-700", red: "text-red-700" };
  return (
    <Card className="border-zinc-200" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-zinc-50"><Icon className="w-4 h-4 text-zinc-600" /></div>
          <div>
            <div className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{label}</div>
            <div className={`text-2xl font-bold ${tones[tone] || tones.default}`}>{value}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AgentCard({ agent, onClick, t }) {
  const Icon = AGENT_ICON[agent.slug] || Bot;
  const status = STATUS_STYLE[agent.status] || STATUS_STYLE.idle;
  return (
    <Card
      className="border-zinc-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={onClick}
      data-testid={`wf-agent-card-${agent.slug}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100"><Icon className="w-5 h-5 text-indigo-700" /></div>
            <div>
              <CardTitle className="text-base font-bold text-zinc-900" data-testid={`wf-agent-name-${agent.slug}`}>{agent.name}</CardTitle>
              <div className="text-xs text-zinc-500">{agent.role}</div>
            </div>
          </div>
          <span className={`text-[10px] font-semibold px-2 py-1 rounded border uppercase tracking-wide ${status.cls}`} data-testid={`wf-agent-status-${agent.slug}`}>{t(status.key)}</span>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-sm text-zinc-600 line-clamp-3">{agent.description}</p>
        <div className="flex items-center gap-4 text-xs text-zinc-500 pt-2 border-t border-zinc-100">
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />{t("wf_field_completed_count", { n: agent.completed_tasks || 0 })}</span>
          <span className="inline-flex items-center gap-1"><Bot className="w-3.5 h-3.5 text-zinc-400" /><code className="text-[10px] text-zinc-500">{agent.display_name || agent.model}</code></span>
        </div>
      </CardContent>
    </Card>
  );
}

function ResearchSourcesSection({ taskId, t }) {
  // Live-web research audit view. Renders the exact URLs Tavily returned +
  // the tool-run log so the admin can verify the AI actually searched
  // instead of hallucinating URLs (Phase 2A "no fake research" guarantee).
  const [sources, setSources] = useState([]);
  const [toolRuns, setToolRuns] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get(`/admin/workforce/research/sources?task_id=${taskId}`),
          api.get(`/admin/workforce/research/tool-runs?task_id=${taskId}`),
        ]);
        if (!alive) return;
        setSources(s.data?.sources || []);
        setToolRuns(r.data?.tool_runs || []);
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, [taskId]);
  if (sources.length === 0 && toolRuns.length === 0) return null;
  const searches = toolRuns.filter((t2) => t2.tool_name === "web_search").length;
  const pages = toolRuns.filter((t2) => t2.tool_name === "safe_page_read" && t2.status === "ok").length;
  return (
    <div className="p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="wf-research-sources">
      <div className="flex items-center gap-2 mb-2">
        <Badge className="bg-amber-600 text-white text-[10px] font-bold uppercase">{t("wf_lwr_badge")}</Badge>
        <span className="text-[11px] text-amber-800">
          {t("wf_lwr_counts", { searches, sources: sources.length, pages })}
        </span>
      </div>
      {sources.length > 0 && (
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="wf-sources-list">
          {sources.map((s) => (
            <div key={s.id} className="p-2 rounded bg-white border border-amber-100" data-testid={`wf-source-${s.source_domain}`}>
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-indigo-700 hover:underline block truncate">
                {s.title || s.url}
              </a>
              <div className="text-[10px] text-zinc-500 mb-0.5">
                <span className="font-mono">{s.source_domain}</span>
                {s.published_at && <span> · {new Date(s.published_at).toLocaleDateString()}</span>}
              </div>
              {s.snippet && <div className="text-[11px] text-zinc-600 line-clamp-2">{s.snippet}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function TaskRow({ task, onClick, t }) {
  const cls = TASK_STATUS_STYLE[task.status] || TASK_STATUS_STYLE.pending;
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-md hover:bg-zinc-50 border border-zinc-200 text-left"
      data-testid={`wf-task-row-${task.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-zinc-900 truncate">{task.title}</div>
        <div className="text-xs text-zinc-500 truncate">{task.description}</div>
      </div>
      <span className={`text-[10px] font-semibold px-2 py-1 rounded border uppercase tracking-wide ${cls}`}>{t(`wf_status_${task.status}`, {}) === `wf_status_${task.status}` ? task.status : t(`wf_status_${task.status}`)}</span>
      <ChevronRight className="w-4 h-4 text-zinc-400" />
    </button>
  );
}

export default function AiWorkforce() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const isAdmin = !!user?.is_admin;

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [agents, setAgents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState(null);

  // Task creation
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDesc, setTaskDesc] = useState("");
  const [creating, setCreating] = useState(false);

  // Detail sheets
  const [openAgent, setOpenAgent] = useState(null); // agent obj
  const [agentTasks, setAgentTasks] = useState([]);
  const [agentMemory, setAgentMemory] = useState([]);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [approvalsOpen, setApprovalsOpen] = useState(false);

  // Phase 2B — Leads state
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [leadsOverview, setLeadsOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadFilters, setLeadFilters] = useState({ status: "", verification: "", min_score: 0, sort: "score" });
  const [openLeadId, setOpenLeadId] = useState(null);
  const [leadDetail, setLeadDetail] = useState(null);

  // Part 2 — CEO Chat state. Kept next to the dashboard so it's the first
  // thing the admin sees. Individual agent chats live inside each agent
  // sheet under a "Chat" tab (see openAgent handling below).
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatConvId, setChatConvId] = useState(null);
  // Per-agent chat (inside employee sheet) — a lightweight parallel state
  const [agentChatMessages, setAgentChatMessages] = useState([]);
  const [agentChatInput, setAgentChatInput] = useState("");
  const [agentChatSending, setAgentChatSending] = useState(false);

  const loadCeoChat = useCallback(async () => {
    try {
      const r = await api.get("/admin/workforce/conversations");
      const conv = (r.data?.conversations || []).find((c) => !c.agent_id);
      if (!conv) { setChatMessages([]); return; }
      setChatConvId(conv.id);
      const m = await api.get(`/admin/workforce/conversations/${conv.id}/messages`);
      setChatMessages(m.data?.messages || []);
    } catch { /* non-fatal */ }
  }, []);

  const loadAgentChat = async (agentId) => {
    try {
      const r = await api.get("/admin/workforce/conversations");
      const conv = (r.data?.conversations || []).find((c) => c.agent_id === agentId);
      if (!conv) { setAgentChatMessages([]); return; }
      const m = await api.get(`/admin/workforce/conversations/${conv.id}/messages`);
      setAgentChatMessages(m.data?.messages || []);
    } catch { /* non-fatal */ }
  };

  const sendChat = async (text, agentSlug = null, isCeo = true) => {
    if (!text.trim()) return;
    if (isCeo) { setChatSending(true); setChatInput(""); }
    else { setAgentChatSending(true); setAgentChatInput(""); }
    // Optimistic append so the user sees their bubble immediately.
    const optimistic = { id: `local-${Date.now()}`, sender_type: "admin", message: text.trim(), created_at: new Date().toISOString() };
    if (isCeo) setChatMessages((m) => [...m, optimistic]);
    else setAgentChatMessages((m) => [...m, optimistic]);
    try {
      const r = await api.post("/admin/workforce/chat", {
        message: text.trim(),
        agent_slug: agentSlug,
        // Phase 2A.5: hand the current Lumioos UI language to the backend
        // so the CEO/agent replies AND any task/CEO summary the intent
        // classifier spawns come back in this language.
        preferred_response_language: lang,
      });
      const reply = r.data?.reply;
      if (reply) {
        if (isCeo) setChatMessages((m) => [...m, reply]);
        else setAgentChatMessages((m) => [...m, reply]);
      }
      // Refresh dashboard so the intent-driven side effect (new task, cancel, approval) is visible immediately.
      await refresh();
    } catch (e) {
      const err = { id: `err-${Date.now()}`, sender_type: "system", message: `${t("wf_chat_error_prefix")} ${e?.response?.data?.detail || t("wf_chat_error_fallback")}`, created_at: new Date().toISOString() };
      if (isCeo) setChatMessages((m) => [...m, err]);
      else setAgentChatMessages((m) => [...m, err]);
    } finally {
      if (isCeo) setChatSending(false);
      else setAgentChatSending(false);
    }
  };

  const refresh = useCallback(async () => {
    // Phase 2B safety: `Promise.all` used to fail the whole dashboard if
    // any single endpoint returned non-200 (e.g. Leads had a hiccup).
    // Switched to `allSettled` so an isolated Lead API failure degrades
    // gracefully — the rest of the workforce keeps rendering.
    const endpoints = [
      { key: "overview",       path: "/admin/workforce/overview",              setter: setOverview,      required: true  },
      { key: "agents",         path: "/admin/workforce/agents",                setter: (d) => setAgents(d || []),               required: true  },
      { key: "tasks",          path: "/admin/workforce/tasks?limit=20",        setter: (d) => setTasks(d?.tasks || []),         required: false },
      { key: "approvals",      path: "/admin/workforce/approvals?status=pending", setter: (d) => setApprovals(d?.approvals || []), required: false },
      { key: "activity",       path: "/admin/workforce/activity?limit=20",     setter: (d) => setActivity(d?.activity || []),   required: false },
      { key: "leads_overview", path: "/admin/workforce/leads/overview",        setter: setLeadsOverview, required: false },
    ];
    const results = await Promise.allSettled(endpoints.map((e) => api.get(e.path)));
    let requiredFailure = null;
    results.forEach((res, idx) => {
      const ep = endpoints[idx];
      if (res.status === "fulfilled") {
        try { ep.setter(res.value.data); } catch { /* ignore setter error */ }
      } else {
        // Log the isolated failure but do NOT wipe the page. Only *required*
        // endpoints (overview, agents) can promote the whole page to an
        // error state; everything else just quietly shows its empty view.
        console.warn(`[AI Workforce] ${ep.key} failed:`, res.reason?.response?.status, res.reason?.message);
        if (ep.required && !requiredFailure) {
          requiredFailure = res.reason?.response?.data?.detail || res.reason?.message;
        }
      }
    });
    if (requiredFailure) {
      setError(requiredFailure || t("wf_toast_workforce_load_fail"));
    } else {
      setError(null);
    }
  }, [t]);

  const loadLeads = useCallback(async () => {
    const qs = new URLSearchParams();
    if (leadFilters.status) qs.set("status", leadFilters.status);
    if (leadFilters.verification) qs.set("verification", leadFilters.verification);
    if (leadFilters.min_score) qs.set("min_score", String(leadFilters.min_score));
    if (leadFilters.sort) qs.set("sort", leadFilters.sort);
    qs.set("limit", "100");
    try {
      const r = await api.get(`/admin/workforce/leads?${qs.toString()}`);
      setLeads(r.data?.leads || []);
    } catch { /* non-fatal */ }
  }, [leadFilters]);

  useEffect(() => { if (leadsOpen) loadLeads(); }, [leadsOpen, loadLeads]);

  const openLeadDetail = async (leadId) => {
    setOpenLeadId(leadId); setLeadDetail(null);
    try {
      const r = await api.get(`/admin/workforce/leads/${leadId}`);
      setLeadDetail(r.data?.lead);
    } catch { /* non-fatal */ }
  };

  const leadAction = async (leadId, action) => {
    try {
      await api.post(`/admin/workforce/leads/${leadId}/${action}`);
      toast.success(t("wf_leads_action_ok", { action: t(`wf_lead_status_${action === "approve" ? "approved" : action === "reject" ? "rejected" : "archived"}`) }));
      await loadLeads();
      await refresh();
      if (openLeadId === leadId) await openLeadDetail(leadId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wf_leads_action_fail", { action }));
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    (async () => { await refresh(); await loadCeoChat(); if (alive) setLoading(false); })();
    const iv = setInterval(async () => { if (alive) await refresh(); }, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [isAdmin, refresh, loadCeoChat]);

  const openAgentDetail = async (agent) => {
    setOpenAgent(agent);
    setAgentTasks([]); setAgentMemory([]); setAgentChatMessages([]);
    try {
      const [t1, m1] = await Promise.all([
        api.get(`/admin/workforce/agents/${agent.id}/tasks?limit=30`),
        api.get(`/admin/workforce/agents/${agent.id}/memory?limit=30`),
      ]);
      setAgentTasks(t1.data?.tasks || []);
      setAgentMemory(m1.data?.memory || []);
      await loadAgentChat(agent.id);
    } catch { /* non-fatal */ }
  };

  const openTaskDetail = async (taskId) => {
    setOpenTaskId(taskId);
    setTaskDetail(null);
    try {
      const r = await api.get(`/admin/workforce/tasks/${taskId}`);
      setTaskDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wf_toast_task_load_fail"));
    }
  };

  const createTask = async () => {
    if (taskTitle.trim().length < 3 || taskDesc.trim().length < 10) {
      toast.error(t("wf_toast_task_short"));
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/admin/workforce/tasks", {
        title: taskTitle.trim(),
        description: taskDesc.trim(),
        priority: "normal",
        // Phase 2A.5: propagate Lumioos UI language into the whole task tree.
        preferred_response_language: lang,
      });
      toast.success(t("wf_toast_task_created"));
      setTaskTitle(""); setTaskDesc("");
      await refresh();
      openTaskDetail(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wf_toast_task_create_fail"));
    } finally {
      setCreating(false);
    }
  };

  const taskAction = async (taskId, action) => {
    try {
      await api.post(`/admin/workforce/tasks/${taskId}/${action}`);
      toast.success(t("wf_toast_task_action_ok", { action }));
      await refresh();
      if (openTaskId === taskId) await openTaskDetail(taskId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wf_toast_task_action_fail", { action }));
    }
  };

  const resolveApproval = async (approvalId, decision, content) => {
    try {
      const url = `/admin/workforce/approvals/${approvalId}/${decision}`;
      const body = decision === "approve" ? { content } : {};
      await api.post(url, body);
      toast.success(decision === "approve" ? t("wf_toast_approved") : t("wf_toast_rejected"));
      await refresh();
      if (openTaskId) await openTaskDetail(openTaskId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wf_toast_approval_fail"));
    }
  };

  if (!isAdmin) return <Navigate to="/app" replace data-testid="wf-non-admin-redirect" />;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ai-workforce-page">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">{t("wf_badge")}</div>
          <h1 className="text-3xl font-bold text-zinc-900">{t("wf_title")}</h1>
          <p className="text-sm text-zinc-600 max-w-2xl">{t("wf_subtitle")}</p>
        </div>
        <Button size="sm" variant="outline" onClick={refresh} data-testid="wf-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {t("wf_refresh")}
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="wf-loading">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      ) : error ? (
        <Card className="border-red-200 bg-red-50" data-testid="wf-error">
          <CardContent className="p-4 text-sm text-red-700">{String(error)}</CardContent>
        </Card>
      ) : (
        <>
          {/* Stats */}
          <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="wf-overview">
            <StatCard icon={Users} label={t("wf_total_agents")} value={overview?.total_agents ?? 0} testid="wf-stat-total-agents" />
            <StatCard icon={Activity} label={t("wf_active_agents")} value={overview?.active_agents ?? 0} tone="emerald" testid="wf-stat-active-agents" />
            <StatCard icon={Loader2} label={t("wf_busy_agents")} value={overview?.busy_agents ?? 0} tone="blue" testid="wf-stat-busy-agents" />
            <StatCard icon={Briefcase} label={t("wf_tasks_in_progress")} value={overview?.tasks_in_progress ?? 0} tone="blue" testid="wf-stat-tasks-in-progress" />
            <StatCard icon={CheckCircle2} label={t("wf_tasks_completed")} value={overview?.tasks_completed ?? 0} tone="emerald" testid="wf-stat-tasks-completed" />
            <StatCard icon={XCircle} label={t("wf_tasks_failed")} value={overview?.tasks_failed ?? 0} tone="red" testid="wf-stat-tasks-failed" />
            <button
              className="text-left"
              onClick={() => setApprovalsOpen(true)}
              data-testid="wf-stat-approvals-btn"
            >
              <StatCard icon={Clock} label={t("wf_approvals_pending")} value={overview?.approvals_pending ?? 0} tone="amber" testid="wf-stat-approvals" />
            </button>
          </section>

          {/* Phase 2B — Leads pipeline card */}
          <button
            className="w-full text-left"
            onClick={() => setLeadsOpen(true)}
            data-testid="wf-open-leads-btn"
          >
            <Card className="border-indigo-200 hover:border-indigo-300 hover:shadow-sm transition-all">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100"><Target className="w-5 h-5 text-indigo-700" /></div>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">{t("wf_leads_overview_title")}</div>
                  <div className="flex items-center gap-4 mt-1 text-sm">
                    <span className="font-bold text-zinc-900">{leadsOverview?.total_leads ?? 0}</span>
                    <span className="text-zinc-500">{t("wf_leads_total")}</span>
                    <span className="text-emerald-700">{leadsOverview?.verified ?? 0} {t("wf_leads_verified")}</span>
                    <span className="text-amber-700">{leadsOverview?.needs_review ?? 0} {t("wf_leads_needs_review")}</span>
                    <span className="text-blue-700">{leadsOverview?.by_status?.new ?? 0} {t("wf_leads_new")}</span>
                    <span className="text-emerald-700">{leadsOverview?.by_status?.approved ?? 0} {t("wf_leads_approved")}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </CardContent>
            </Card>
          </button>

          {/* CEO Chat — primary conversational surface. Sits between stats
              and the task form so the admin can just type an objective and
              have it created from natural language. */}
          <Card className="border-indigo-200 bg-gradient-to-br from-white to-indigo-50/30" data-testid="wf-ceo-chat-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Crown className="w-4 h-4 text-indigo-600" /> {t("wf_ceo_chat_title")}
                <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 font-normal text-[10px] ml-auto">
                  {agents.find((a) => a.slug === "ceo")?.display_name || "GPT-5.6 Sol"}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="max-h-80 min-h-32 overflow-y-auto space-y-2 px-1" data-testid="wf-ceo-chat-messages">
                {chatMessages.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-6 text-center">
                    {t("wf_ceo_chat_empty")}
                  </p>
                ) : chatMessages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === "admin" ? "justify-end" : "justify-start"}`} data-testid={`wf-ceo-msg-${m.sender_type}`}>
                    <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      m.sender_type === "admin" ? "bg-indigo-600 text-white" :
                      m.sender_type === "system" ? "bg-red-50 border border-red-200 text-red-700" :
                      "bg-white border border-zinc-200 text-zinc-800"
                    }`}>
                      <div className="whitespace-pre-wrap">{m.message}</div>
                      {m.task_id && (
                        <button onClick={() => openTaskDetail(m.task_id)} className="mt-1.5 text-[11px] font-semibold underline opacity-90 hover:opacity-100">
                          {t("wf_chat_view_task")}
                        </button>
                      )}
                      <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString()}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(chatInput, null, true); } }}
                  placeholder={t("wf_ceo_chat_placeholder")}
                  disabled={chatSending}
                  className="flex-1 rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  data-testid="wf-ceo-chat-input"
                />
                <Button
                  onClick={() => sendChat(chatInput, null, true)}
                  disabled={chatSending || chatInput.trim().length === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="wf-ceo-chat-send"
                >
                  {chatSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-zinc-500">
                {t("wf_ceo_chat_hint")}
              </p>
            </CardContent>
          </Card>

          {/* Task creation + list */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-zinc-200" data-testid="wf-create-task-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" /> {t("wf_new_objective")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <input
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder={t("wf_task_title_placeholder")}
                  maxLength={200}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="wf-task-title-input"
                />
                <textarea
                  value={taskDesc}
                  onChange={(e) => setTaskDesc(e.target.value)}
                  placeholder={t("wf_task_desc_placeholder")}
                  rows={4}
                  maxLength={4000}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="wf-task-desc-input"
                />
                <Button
                  onClick={createTask}
                  disabled={creating || taskTitle.trim().length < 3 || taskDesc.trim().length < 10}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="wf-create-task-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  {t("wf_send_to_ceo")}
                </Button>
                <p className="text-[11px] text-zinc-500">{t("wf_ceo_will_plan", { model: agents.find((a) => a.slug === "ceo")?.display_name || agents.find((a) => a.slug === "ceo")?.model || "GPT-5.6 Sol" })}</p>
              </CardContent>
            </Card>

            <Card className="border-zinc-200" data-testid="wf-tasks-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-indigo-600" /> {t("wf_recent_tasks")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {tasks.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-4 text-center">{t("wf_no_tasks")}</p>
                ) : (
                  tasks.map((tk) => <TaskRow key={tk.id} task={tk} t={t} onClick={() => openTaskDetail(tk.id)} />)
                )}
              </CardContent>
            </Card>
          </section>

          {/* Agents */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-zinc-900">{t("wf_agents_title")}</h2>
              <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 font-normal">{t("wf_workspace_company")}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wf-agents-grid">
              {agents.map((a) => <AgentCard key={a.id} agent={a} t={t} onClick={() => openAgentDetail(a)} />)}
            </div>
          </section>

          {/* Activity */}
          <Card className="border-zinc-200" data-testid="wf-activity-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold flex items-center gap-2"><Activity className="w-4 h-4 text-indigo-600" /> {t("wf_recent_activity")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 max-h-72 overflow-y-auto">
              {activity.length === 0 ? (
                <p className="text-sm text-zinc-500 py-3 text-center">{t("wf_no_activity")}</p>
              ) : (
                activity.map((a) => (
                  <div key={a.id} className="flex items-start gap-2 text-xs py-1.5 border-b border-zinc-100 last:border-0" data-testid={`wf-activity-${a.kind}`}>
                    <span className="font-mono text-[10px] text-zinc-400 shrink-0 mt-0.5">{new Date(a.created_at).toLocaleTimeString()}</span>
                    <Badge className="bg-zinc-100 text-zinc-700 font-mono text-[10px] font-normal">{a.kind}</Badge>
                    <span className="text-zinc-700 flex-1">{a.detail}</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ================= EMPLOYEE DETAIL SHEET ================= */}
      <Sheet open={!!openAgent} onOpenChange={(v) => !v && setOpenAgent(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="wf-agent-sheet">
          {openAgent && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {(() => { const Ic = AGENT_ICON[openAgent.slug] || Bot; return <Ic className="w-5 h-5 text-indigo-600" />; })()}
                  {openAgent.name}
                </SheetTitle>
                <div className="text-xs text-zinc-500">{openAgent.role}</div>
              </SheetHeader>
              <Tabs defaultValue="overview" className="mt-4">
                <TabsList className="grid grid-cols-7 w-full">
                  <TabsTrigger value="overview" data-testid="wf-agent-tab-overview">{t("wf_tab_overview")}</TabsTrigger>
                  <TabsTrigger value="chat" data-testid="wf-agent-tab-chat">{t("wf_tab_chat")}</TabsTrigger>
                  <TabsTrigger value="current" data-testid="wf-agent-tab-current">{t("wf_tab_current")}</TabsTrigger>
                  <TabsTrigger value="history" data-testid="wf-agent-tab-history">{t("wf_tab_history")}</TabsTrigger>
                  <TabsTrigger value="activity" data-testid="wf-agent-tab-activity">{t("wf_tab_activity")}</TabsTrigger>
                  <TabsTrigger value="memory" data-testid="wf-agent-tab-memory">{t("wf_tab_memory")}</TabsTrigger>
                  <TabsTrigger value="config" data-testid="wf-agent-tab-config">{t("wf_tab_config")}</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="mt-3 space-y-3 text-sm">
                  <p className="text-zinc-700">{openAgent.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded-md bg-zinc-50"><div className="text-[10px] text-zinc-500 uppercase">{t("wf_field_status")}</div><div className="font-semibold">{t(`wf_status_${openAgent.status}`, {}) === `wf_status_${openAgent.status}` ? openAgent.status : t(`wf_status_${openAgent.status}`)}</div></div>
                    <div className="p-2 rounded-md bg-zinc-50"><div className="text-[10px] text-zinc-500 uppercase">{t("wf_field_completed")}</div><div className="font-semibold">{openAgent.completed_tasks}</div></div>
                    <div className="p-2 rounded-md bg-zinc-50 col-span-2"><div className="text-[10px] text-zinc-500 uppercase">{t("wf_field_last_activity")}</div><div className="font-mono text-xs">{openAgent.last_activity_at || t("wf_field_none")}</div></div>
                  </div>
                </TabsContent>

                <TabsContent value="chat" className="mt-3 space-y-2 text-sm" data-testid="wf-agent-chat-panel">
                  <div className="max-h-72 min-h-32 overflow-y-auto space-y-2 border border-zinc-200 rounded-md p-2 bg-zinc-50">
                    {agentChatMessages.length === 0 ? (
                      <p className="text-xs text-zinc-500 text-center py-4">{t("wf_agent_chat_empty", { name: openAgent.name })}</p>
                    ) : agentChatMessages.map((m) => (
                      <div key={m.id} className={`flex ${m.sender_type === "admin" ? "justify-end" : "justify-start"}`} data-testid={`wf-agent-msg-${m.sender_type}`}>
                        <div className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${
                          m.sender_type === "admin" ? "bg-indigo-600 text-white" :
                          m.sender_type === "system" ? "bg-red-50 border border-red-200 text-red-700" :
                          "bg-white border border-zinc-200 text-zinc-800"
                        }`}>
                          <div className="whitespace-pre-wrap">{m.message}</div>
                          {m.task_id && (
                            <button onClick={() => { setOpenAgent(null); openTaskDetail(m.task_id); }} className="mt-1 text-[10px] underline opacity-90">{t("wf_chat_view_task")}</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      value={agentChatInput}
                      onChange={(e) => setAgentChatInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(agentChatInput, openAgent.slug, false); } }}
                      placeholder={t("wf_agent_chat_placeholder", { name: openAgent.name })}
                      disabled={agentChatSending}
                      className="flex-1 rounded-md border border-zinc-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid="wf-agent-chat-input"
                    />
                    <Button
                      onClick={() => sendChat(agentChatInput, openAgent.slug, false)}
                      disabled={agentChatSending || agentChatInput.trim().length === 0}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"
                      data-testid="wf-agent-chat-send"
                    >
                      {agentChatSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </TabsContent>
                <TabsContent value="current" className="mt-3 space-y-2 text-sm">
                  {agentTasks.filter((tk) => !["completed", "failed", "cancelled"].includes(tk.status)).length === 0 ? (
                    <p className="text-zinc-500 text-xs py-3">{t("wf_no_active_tasks")}</p>
                  ) : (
                    agentTasks.filter((tk) => !["completed", "failed", "cancelled"].includes(tk.status)).map((tk) => (
                      <TaskRow key={tk.id} task={tk} t={t} onClick={() => { setOpenAgent(null); openTaskDetail(tk.parent_task_id || tk.id); }} />
                    ))
                  )}
                </TabsContent>
                <TabsContent value="history" className="mt-3 space-y-2 text-sm">
                  {agentTasks.filter((tk) => ["completed", "failed", "cancelled"].includes(tk.status)).length === 0 ? (
                    <p className="text-zinc-500 text-xs py-3">{t("wf_no_history")}</p>
                  ) : (
                    agentTasks.filter((tk) => ["completed", "failed", "cancelled"].includes(tk.status)).map((tk) => (
                      <TaskRow key={tk.id} task={tk} t={t} onClick={() => { setOpenAgent(null); openTaskDetail(tk.parent_task_id || tk.id); }} />
                    ))
                  )}
                </TabsContent>
                <TabsContent value="activity" className="mt-3 space-y-1 text-xs">
                  {activity.filter((a) => a.agent_id === openAgent.id).length === 0 ? (
                    <p className="text-zinc-500 py-3">{t("wf_no_activity")}</p>
                  ) : (
                    activity.filter((a) => a.agent_id === openAgent.id).map((a) => (
                      <div key={a.id} className="py-1.5 border-b border-zinc-100 last:border-0">
                        <span className="font-mono text-[10px] text-zinc-400 mr-2">{new Date(a.created_at).toLocaleString()}</span>
                        <span className="text-zinc-700">{a.detail}</span>
                      </div>
                    ))
                  )}
                </TabsContent>
                <TabsContent value="memory" className="mt-3 space-y-2 text-xs">
                  {agentMemory.length === 0 ? (
                    <p className="text-zinc-500 py-3">{t("wf_no_memory")}</p>
                  ) : (
                    agentMemory.map((m) => (
                      <div key={m.id} className="p-2 rounded-md border border-zinc-200 bg-zinc-50" data-testid={`wf-memory-item-${m.memory_type}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-indigo-50 text-indigo-700 text-[10px] font-normal">{m.memory_type}</Badge>
                          <span className="font-mono text-[10px] text-zinc-400">{new Date(m.created_at).toLocaleString()}</span>
                        </div>
                        <p className="whitespace-pre-wrap text-zinc-700 line-clamp-6">{m.content}</p>
                      </div>
                    ))
                  )}
                </TabsContent>
                <TabsContent value="config" className="mt-3 space-y-2 text-xs">
                  <div className="p-2 rounded-md bg-zinc-50">
                    <div className="text-[10px] text-zinc-500 uppercase mb-1">{t("wf_cfg_model")}</div>
                    <div className="font-semibold text-sm">{openAgent.display_name || openAgent.model}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      key: <code>{openAgent.model_key || openAgent.model}</code> · provider: <code>{openAgent.provider || "?"}</code> · id: <code>{openAgent.provider_model_id || openAgent.model}</code>
                    </div>
                  </div>
                  <div className="p-2 rounded-md bg-zinc-50"><div className="text-[10px] text-zinc-500 uppercase mb-1">{t("wf_cfg_allowed_tools")}</div><code className="text-[10px]">{JSON.stringify(openAgent.allowed_tools)}</code></div>
                  <div className="p-2 rounded-md bg-zinc-50"><div className="text-[10px] text-zinc-500 uppercase mb-1">{t("wf_cfg_system_instructions")}</div><p className="whitespace-pre-wrap">{openAgent.system_instructions}</p></div>
                  <div className="p-2 rounded-md bg-zinc-50"><div className="text-[10px] text-zinc-500 uppercase mb-1">{t("wf_cfg_agent_id")}</div><code className="text-[10px]">{openAgent.id}</code></div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ================= TASK DETAIL SHEET ================= */}
      <Sheet open={!!openTaskId} onOpenChange={(v) => !v && setOpenTaskId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="wf-task-sheet">
          {!taskDetail ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle className="pr-8">{taskDetail.task.title}</SheetTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[10px] font-normal border ${TASK_STATUS_STYLE[taskDetail.task.status]}`}>{t(`wf_status_${taskDetail.task.status}`, {}) === `wf_status_${taskDetail.task.status}` ? taskDetail.task.status : t(`wf_status_${taskDetail.task.status}`)}</Badge>
                  <span className="text-[10px] font-mono text-zinc-500">{taskDetail.task.id}</span>
                </div>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div>
                  <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">{t("wf_task_objective")}</div>
                  <p className="text-zinc-700 whitespace-pre-wrap">{taskDetail.task.description}</p>
                </div>
                {taskDetail.task.plan_summary && (
                  <div className="p-3 rounded-md bg-indigo-50 border border-indigo-100">
                    <div className="text-[10px] font-semibold text-indigo-700 uppercase mb-1">{t("wf_task_ceo_plan")}</div>
                    <p className="text-zinc-800 whitespace-pre-wrap" data-testid="wf-task-plan">{taskDetail.task.plan_summary}</p>
                  </div>
                )}
                <div>
                  <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-2">{t("wf_task_children", { n: taskDetail.children.length })}</div>
                  <div className="space-y-2">
                    {taskDetail.children.map((c) => {
                      // Use the task-scoped approvals list (all statuses) so
                      // cancelled ones still show a status badge for audit.
                      const relatedApproval = (taskDetail.approvals || []).find((ap) => ap.task_id === c.id);
                      return (
                        <div key={c.id} className="p-3 rounded-md border border-zinc-200 bg-white" data-testid={`wf-child-${c.assigned_agent_slug}`}>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              {(() => { const Ic = AGENT_ICON[c.assigned_agent_slug] || Bot; return <Ic className="w-4 h-4 text-indigo-600" />; })()}
                              <strong className="text-sm">{c.title}</strong>
                            </div>
                            <Badge className={`text-[10px] font-normal border ${TASK_STATUS_STYLE[c.status]}`}>{t(`wf_status_${c.status}`, {}) === `wf_status_${c.status}` ? c.status : t(`wf_status_${c.status}`)}</Badge>
                          </div>
                          <p className="text-xs text-zinc-600 mb-2">{c.description}</p>
                          {c.result && (
                            <details className="text-xs">
                              <summary className="cursor-pointer text-zinc-500 hover:text-zinc-700">{t("wf_task_view_result", { n: c.result.length })}</summary>
                              <div className="mt-2 p-2 rounded bg-zinc-50 whitespace-pre-wrap text-zinc-700">{c.result}</div>
                            </details>
                          )}
                          {c.error && <div className="text-xs text-red-700 mt-1">{t("wf_task_error_prefix")} {c.error}</div>}
                          {relatedApproval && relatedApproval.status === "pending" && (
                            <div className="mt-2 flex items-center gap-2">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" onClick={() => resolveApproval(relatedApproval.id, "approve", relatedApproval.content)} data-testid={`wf-approve-${c.assigned_agent_slug}`}>
                                <ThumbsUp className="w-3 h-3 mr-1" /> {t("wf_approve")}
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => resolveApproval(relatedApproval.id, "reject")} data-testid={`wf-reject-${c.assigned_agent_slug}`}>
                                <ThumbsDown className="w-3 h-3 mr-1" /> {t("wf_reject")}
                              </Button>
                            </div>
                          )}
                          {relatedApproval && relatedApproval.status !== "pending" && (
                            // Cancelled / already-resolved approvals stay visible for
                            // audit but are non-actionable. Prevents the production bug
                            // where Approve/Reject buttons appeared on a cancelled tree.
                            <div className="mt-2">
                              <Badge className="bg-zinc-100 text-zinc-500 text-[10px] font-normal border border-zinc-200" data-testid={`wf-approval-status-${c.assigned_agent_slug}`}>
                                {t("wf_approval_status_prefix", { status: t(`wf_status_${relatedApproval.status}`, {}) === `wf_status_${relatedApproval.status}` ? relatedApproval.status : t(`wf_status_${relatedApproval.status}`) })}
                              </Badge>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {taskDetail.task.result && (
                  <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                    <div className="text-[10px] font-semibold text-emerald-700 uppercase mb-1">{t("wf_task_ceo_summary")}</div>
                    <div className="whitespace-pre-wrap text-zinc-800" data-testid="wf-task-result">{taskDetail.task.result}</div>
                  </div>
                )}
                {/* Phase 2A: live-web sources for research/sales tasks */}
                {(taskDetail.children.some((c) => ["research","sales"].includes(c.assigned_agent_slug)) ||
                  ["research","sales"].includes(taskDetail.task.assigned_agent_slug)) && (
                  <ResearchSourcesSection taskId={taskDetail.task.id} t={t} />
                )}
                {taskDetail.task.error && (
                  <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs" data-testid="wf-task-error">
                    <strong>{t("wf_task_error_prefix")}</strong> {taskDetail.task.error}
                  </div>
                )}
                {taskDetail.messages.length > 0 && (
                  <details>
                    <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-700 flex items-center gap-1">
                      <MessageSquare className="w-3.5 h-3.5" /> {t("wf_task_agent_messages", { n: taskDetail.messages.length })}
                    </summary>
                    <div className="mt-2 space-y-1 text-xs">
                      {taskDetail.messages.map((m) => (
                        <div key={m.id} className="p-2 rounded bg-zinc-50 border border-zinc-200" data-testid={`wf-message-${m.from_agent}-${m.to_agent}`}>
                          <div className="font-mono text-[10px] text-zinc-500 mb-0.5">{m.from_agent} → {m.to_agent} · {new Date(m.created_at).toLocaleTimeString()}</div>
                          <div className="text-zinc-700 whitespace-pre-wrap line-clamp-4">{m.message}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {/* Action buttons */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  {taskDetail.task.status === "in_progress" && (
                    <Button size="sm" variant="outline" onClick={() => taskAction(taskDetail.task.id, "pause")} data-testid="wf-task-pause-btn">
                      <Pause className="w-3.5 h-3.5 mr-1" /> {t("wf_task_pause")}
                    </Button>
                  )}
                  {taskDetail.task.status === "paused" && (
                    <Button size="sm" variant="outline" onClick={() => taskAction(taskDetail.task.id, "resume")} data-testid="wf-task-resume-btn">
                      <Play className="w-3.5 h-3.5 mr-1" /> {t("wf_task_resume")}
                    </Button>
                  )}
                  {taskDetail.task.status === "failed" && (
                    <Button size="sm" variant="outline" onClick={() => taskAction(taskDetail.task.id, "retry")} data-testid="wf-task-retry-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> {t("wf_task_retry")}
                    </Button>
                  )}
                  {!["completed", "cancelled", "failed"].includes(taskDetail.task.status) && (
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => taskAction(taskDetail.task.id, "cancel")} data-testid="wf-task-cancel-btn">
                      <X className="w-3.5 h-3.5 mr-1" /> {t("wf_task_cancel")}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ================= APPROVALS SHEET ================= */}
      <Sheet open={approvalsOpen} onOpenChange={setApprovalsOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="wf-approvals-sheet">
          <SheetHeader>
            <SheetTitle>{t("wf_approvals_title", { n: approvals.length })}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {approvals.length === 0 ? (
              <p className="text-sm text-zinc-500 py-6 text-center">{t("wf_no_approvals")}</p>
            ) : (
              approvals.map((ap) => (
                <Card key={ap.id} className="border-amber-200" data-testid={`wf-approval-${ap.id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {(() => { const Ic = AGENT_ICON[ap.agent_slug] || Bot; return <Ic className="w-4 h-4 text-indigo-600" />; })()}
                        <strong className="text-sm">{ap.agent_slug}</strong>
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500">{new Date(ap.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-zinc-600">{ap.reason}</p>
                    <div className="p-2 rounded bg-zinc-50 text-xs whitespace-pre-wrap max-h-60 overflow-y-auto">{ap.content}</div>
                    <div className="flex items-center gap-2 pt-1">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" onClick={() => resolveApproval(ap.id, "approve", ap.content)} data-testid={`wf-approval-approve-${ap.id}`}>
                        <ThumbsUp className="w-3 h-3 mr-1" /> {t("wf_approve")}
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => resolveApproval(ap.id, "reject")} data-testid={`wf-approval-reject-${ap.id}`}>
                        <ThumbsDown className="w-3 h-3 mr-1" /> {t("wf_reject")}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ================= LEADS SHEET (Phase 2B) ================= */}
      <Sheet open={leadsOpen} onOpenChange={setLeadsOpen}>
        <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto" data-testid="wf-leads-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" /> {t("wf_leads_title")} ({leadsOverview?.total_leads ?? 0})
            </SheetTitle>
          </SheetHeader>

          {/* Filters */}
          <div className="mt-4 flex flex-wrap items-end gap-2 text-xs" data-testid="wf-leads-filters">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-zinc-500 uppercase">{t("wf_leads_filter_status")}</label>
              <select className="border border-zinc-300 rounded px-2 py-1" value={leadFilters.status}
                      onChange={(e) => setLeadFilters((f) => ({ ...f, status: e.target.value }))}
                      data-testid="wf-leads-filter-status">
                <option value="">{t("wf_leads_filter_all")}</option>
                {["new","qualified","reviewing","approved","rejected","archived"].map((s) =>
                  <option key={s} value={s}>{t(`wf_lead_status_${s}`)}</option>
                )}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-zinc-500 uppercase">{t("wf_leads_filter_verification")}</label>
              <select className="border border-zinc-300 rounded px-2 py-1" value={leadFilters.verification}
                      onChange={(e) => setLeadFilters((f) => ({ ...f, verification: e.target.value }))}
                      data-testid="wf-leads-filter-verification">
                <option value="">{t("wf_leads_filter_all")}</option>
                <option value="verified">{t("wf_verif_verified")}</option>
                <option value="needs_review">{t("wf_verif_needs_review")}</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-zinc-500 uppercase">{t("wf_leads_filter_min_score")}</label>
              <input type="number" min={0} max={100} value={leadFilters.min_score}
                     onChange={(e) => setLeadFilters((f) => ({ ...f, min_score: Number(e.target.value) || 0 }))}
                     className="border border-zinc-300 rounded px-2 py-1 w-20"
                     data-testid="wf-leads-filter-min-score" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-zinc-500 uppercase">{t("wf_leads_sort")}</label>
              <select className="border border-zinc-300 rounded px-2 py-1" value={leadFilters.sort}
                      onChange={(e) => setLeadFilters((f) => ({ ...f, sort: e.target.value }))}
                      data-testid="wf-leads-filter-sort">
                <option value="score">{t("wf_leads_sort_score")}</option>
                <option value="newest">{t("wf_leads_sort_newest")}</option>
                <option value="name">{t("wf_leads_sort_name")}</option>
              </select>
            </div>
          </div>

          {/* Leads list */}
          <div className="mt-3 space-y-2" data-testid="wf-leads-list">
            {leads.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-8">{t("wf_leads_none")}</p>
            ) : leads.map((L) => (
              <button key={L.id} onClick={() => openLeadDetail(L.id)}
                      className="w-full text-left p-3 rounded-md border border-zinc-200 hover:border-indigo-300 hover:bg-zinc-50 transition-all"
                      data-testid={`wf-lead-row-${L.normalized_domain}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <strong className="text-sm text-zinc-900 truncate">{L.company_name}</strong>
                      <span className="font-mono text-[10px] text-zinc-500 truncate">{L.normalized_domain}</span>
                    </div>
                    <div className="text-xs text-zinc-600 line-clamp-1">{L.company_description || "—"}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      {L.industry || "—"} · {L.location || "—"}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <div className={`text-lg font-bold ${L.fit_score >= 70 ? "text-emerald-700" : L.fit_score >= 40 ? "text-amber-700" : "text-zinc-500"}`}>
                      {L.fit_score}
                    </div>
                    <Badge className={`text-[10px] font-normal border ${L.verification_status === "verified" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {L.verification_status === "verified" ? t("wf_verif_verified") : t("wf_verif_needs_review")}
                    </Badge>
                    <Badge className="text-[10px] font-normal border bg-zinc-100 text-zinc-700 border-zinc-200">
                      {t(`wf_lead_status_${L.status || "new"}`)}
                    </Badge>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* ================= LEAD DETAIL SHEET (Phase 2B) ================= */}
      <Sheet open={!!openLeadId} onOpenChange={(v) => !v && setOpenLeadId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="wf-lead-detail-sheet">
          {!leadDetail ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle className="pr-8">{leadDetail.company_name}</SheetTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[10px] font-normal border ${leadDetail.verification_status === "verified" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {leadDetail.verification_status === "verified" ? t("wf_verif_verified") : t("wf_verif_needs_review")}
                  </Badge>
                  <Badge className="text-[10px] font-normal border bg-zinc-100 text-zinc-700 border-zinc-200">
                    {t(`wf_lead_status_${leadDetail.status || "new"}`)}
                  </Badge>
                  <span className={`ml-auto text-2xl font-bold ${leadDetail.fit_score >= 70 ? "text-emerald-700" : leadDetail.fit_score >= 40 ? "text-amber-700" : "text-zinc-500"}`}>
                    {leadDetail.fit_score}/100
                  </span>
                </div>
              </SheetHeader>

              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-md bg-zinc-50">
                    <div className="text-[10px] text-zinc-500 uppercase">{t("wf_leads_col_website")}</div>
                    <a href={leadDetail.website} target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:underline text-xs flex items-center gap-1 truncate">
                      {leadDetail.website} <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  </div>
                  <div className="p-2 rounded-md bg-zinc-50">
                    <div className="text-[10px] text-zinc-500 uppercase">{t("wf_leads_col_industry")}</div>
                    <div className="text-xs">{leadDetail.industry || "—"}</div>
                  </div>
                  <div className="p-2 rounded-md bg-zinc-50">
                    <div className="text-[10px] text-zinc-500 uppercase">{t("wf_leads_col_location")}</div>
                    <div className="text-xs">{leadDetail.location || "—"}</div>
                  </div>
                  <div className="p-2 rounded-md bg-zinc-50">
                    <div className="text-[10px] text-zinc-500 uppercase">Company size</div>
                    <div className="text-xs">{leadDetail.company_size_estimate || "unknown"}</div>
                  </div>
                </div>

                {leadDetail.company_description && (
                  <div>
                    <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Description</div>
                    <p className="text-zinc-700">{leadDetail.company_description}</p>
                  </div>
                )}

                {leadDetail.fit_reasons?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">{t("wf_leads_score_reasons")}</div>
                    <ul className="list-disc ml-5 text-xs text-zinc-700 space-y-0.5" data-testid="wf-lead-reasons">
                      {leadDetail.fit_reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}

                {leadDetail.concerns?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">{t("wf_leads_concerns")}</div>
                    <ul className="list-disc ml-5 text-xs text-zinc-700 space-y-0.5">
                      {leadDetail.concerns.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}

                {leadDetail.source_urls?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">{t("wf_leads_sources")}</div>
                    <div className="space-y-1" data-testid="wf-lead-sources">
                      {leadDetail.source_urls.map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="block text-xs text-indigo-700 hover:underline truncate">
                          {u}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {leadDetail.source_task_id && (
                  <div>
                    <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">{t("wf_leads_discovery_task")}</div>
                    <button className="text-xs text-indigo-700 hover:underline font-mono" onClick={() => { setOpenLeadId(null); setLeadsOpen(false); openTaskDetail(leadDetail.source_task_id); }}>
                      {leadDetail.source_task_id}
                    </button>
                  </div>
                )}

                <p className="text-[10px] text-zinc-500 italic">{t("wf_leads_no_outreach_note")}</p>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                          onClick={() => leadAction(leadDetail.id, "approve")}
                          data-testid="wf-lead-approve-btn">
                    <ThumbsUp className="w-3.5 h-3.5 mr-1" /> {t("wf_leads_actions_approve")}
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs"
                          onClick={() => leadAction(leadDetail.id, "reject")}
                          data-testid="wf-lead-reject-btn">
                    <ThumbsDown className="w-3.5 h-3.5 mr-1" /> {t("wf_leads_actions_reject")}
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs"
                          onClick={() => leadAction(leadDetail.id, "archive")}
                          data-testid="wf-lead-archive-btn">
                    <Archive className="w-3.5 h-3.5 mr-1" /> {t("wf_leads_actions_archive")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
